import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MapPin, Phone, ArrowUpRight, ShieldCheck, Search, X, Compass } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';
import Pagination from '../components/Pagination';
import SearchAutocomplete from '../components/SearchAutocomplete';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const MyFavorites = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Dropdown list states
  const [states, setStates] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [categories, setCategories] = useState([]);

  // Filter input states
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Applied filter state for API queries
  const [appliedFilters, setAppliedFilters] = useState({
    state: '',
    city: '',
    categoryId: ''
  });

  // Listings data states
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(0);
  const [removingId, setRemovingId] = useState(null);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  // Load locations and categories metadata on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const locRes = await vendorService.getPublicLocations();
        if (locRes.success && locRes.data) {
          setStates(locRes.data.states || []);
          setCitiesMap(locRes.data.cities || {});
        }

        const catRes = await vendorService.getCategories(false, 'ALL', 'VENDOR');
        if (catRes.success && catRes.data) {
          setCategories(catRes.data || []);
        }
      } catch (err) {
        console.error('Error loading metadata in favorites:', err);
      }
    };
    loadMetadata();
  }, []);

  // Compute available cities dependent on selected state
  const availableCities = selectedState
    ? (citiesMap[selectedState] || [])
    : Array.from(new Set(Object.values(citiesMap).flat()));

  // Fetch paginated & filtered favorites
  const fetchFavorites = useCallback(async (page = 0, currentFilters = appliedFilters) => {
    setLoading(true);
    try {
      const res = await vendorService.getMyFavorites(page, pageSize, currentFilters);
      if (res.success && res.data) {
        setFavorites(res.data.favorites || []);
        setTotalCount(res.data.total || 0);
        setTotalPages(res.data.totalPages || 0);
        setCurrentPage(res.data.page || 0);
      } else {
        setFavorites([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error('Error fetching favorites:', err);
      toast.error('Failed to load your favorite listings');
    } finally {
      setLoading(false);
    }
  }, [pageSize, appliedFilters, toast]);

  useEffect(() => {
    fetchFavorites(currentPage, appliedFilters);
  }, [currentPage, appliedFilters, fetchFavorites]);

  // Apply filters
  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    setAppliedFilters({
      state: selectedState,
      city: selectedCity,
      categoryId: selectedCategory
    });
    setCurrentPage(0);
  };

  const hasActiveFilters = Boolean(appliedFilters.state || appliedFilters.city || appliedFilters.categoryId);

  const handleClearAndReset = () => {
    setSelectedState('');
    setSelectedCity('');
    setSelectedCategory('');
    setAppliedFilters({
      state: '',
      city: '',
      categoryId: ''
    });
    setCurrentPage(0);
  };

  const handleRemoveFavorite = async (listingId, e) => {
    if (e) e.stopPropagation();
    if (removingId) return;

    setRemovingId(listingId);
    try {
      const res = await vendorService.toggleFavorite(listingId);
      if (res.success) {
        toast.info(res.data?.message || 'Removed from favorites');
        // Remove from local list immediately
        setFavorites(prev => prev.filter(item => item.listingId !== listingId));
        setTotalCount(prev => Math.max(0, prev - 1));
      } else {
        toast.error(res.error || 'Failed to remove favorite');
      }
    } catch (err) {
      toast.error(err.message || 'Error removing favorite');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header section (Browse More button placed on top-right for both mobile & desktop) */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex justify-between items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Favorites</h1>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-100 flex-shrink-0">
              {totalCount} {totalCount === 1 ? 'Listing' : 'Listings'}
            </span>
          </div>

          <button
            onClick={() => navigate('/listing')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold bg-[#eaf7f4] hover:bg-[#d8f2eb] text-[#2ab391] border border-[#2ab391]/25 transition-all shadow-2xs hover:shadow-xs cursor-pointer flex-shrink-0"
          >
            <Compass size={14} className="text-[#2ab391]" />
            <span>Browse More</span>
          </button>
        </div>
        <p className="text-gray-500 text-xs sm:text-sm mt-1">
          Listings and vendors you have bookmarked for quick access.
        </p>
      </div>

      {/* Desktop Search Filter Section */}
      <form
        onSubmit={handleApplyFilters}
        className="hidden md:flex bg-white rounded-2xl shadow-sm border border-gray-100 p-4 gap-3 items-center justify-between"
      >
        {/* State Select */}
        <div className="flex-1 min-w-0">
          <SearchAutocomplete
            placeholder="State"
            value={selectedState}
            options={states}
            onSelect={(val) => {
              setSelectedState(val);
              setSelectedCity('');
            }}
            inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
            dropdownClassName="left-0 min-w-full shadow-xl rounded-xl border border-gray-150"
          />
        </div>

        {/* City Select */}
        <div className="flex-1 min-w-0">
          <SearchAutocomplete
            placeholder="City"
            value={selectedCity}
            options={availableCities}
            onSelect={(val) => {
              setSelectedCity(val);
              if (val) {
                for (const state in citiesMap) {
                  if (citiesMap[state]?.includes(val)) {
                    setSelectedState(state);
                    break;
                  }
                }
              }
            }}
            inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
            dropdownClassName="left-0 min-w-full shadow-xl rounded-xl border border-gray-150"
          />
        </div>

        {/* Category Select */}
        <div className="flex-1 min-w-0">
          <SearchAutocomplete
            placeholder="Category"
            value={selectedCategory}
            options={categories}
            onSelect={(val) => setSelectedCategory(val)}
            inputClassName="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer font-medium placeholder-gray-500"
            dropdownClassName="left-0 min-w-full shadow-xl rounded-xl border border-gray-150"
          />
        </div>

        {/* Search Action Button */}
        <div className="flex-shrink-0">
          <button
            type="submit"
            className="bg-[#2ab391] hover:bg-[#23977a] text-white p-2.5 rounded-xl flex items-center justify-center gap-2 font-medium px-6 transition-colors shadow-sm cursor-pointer text-xs sm:text-sm"
            title="Search"
          >
            <Search size={16} strokeWidth={2.5} />
            <span>Search</span>
          </button>
        </div>
      </form>

      {/* Mobile Search Section (Single-row State | City | Category | Search Button) */}
      <form
        onSubmit={handleApplyFilters}
        className="block md:hidden bg-white rounded-xl shadow-md p-1 w-full border border-gray-150"
      >
        <div className="flex items-center w-full gap-1">
          {/* State Select */}
          <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
            <SearchAutocomplete
              placeholder="State"
              value={selectedState}
              options={states}
              onSelect={(val) => {
                setSelectedState(val);
                setSelectedCity('');
              }}
              dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
              inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
            />
          </div>

          {/* City Select */}
          <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
            <SearchAutocomplete
              placeholder="City"
              value={selectedCity}
              options={availableCities}
              onSelect={(val) => {
                setSelectedCity(val);
                if (val) {
                  for (const state in citiesMap) {
                    if (citiesMap[state]?.includes(val)) {
                      setSelectedState(state);
                      break;
                    }
                  }
                }
              }}
              dropdownClassName="left-0 right-auto min-w-[190px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
              inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
            />
          </div>

          {/* Category Select */}
          <div className="flex-[1.2] min-w-0 relative flex items-center pr-1">
            <SearchAutocomplete
              placeholder="Category"
              value={selectedCategory}
              options={categories}
              onSelect={(val) => setSelectedCategory(val)}
              dropdownClassName="right-0 left-auto min-w-[210px] sm:min-w-full shadow-xl rounded-xl border border-gray-150"
              inputClassName="w-full bg-transparent pl-2 pr-6 py-2 text-[11px] text-gray-800 focus:outline-none cursor-pointer font-medium placeholder-gray-500"
            />
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="bg-[#2ab391] text-white rounded-lg hover:bg-opacity-90 transition-all flex items-center justify-center w-9 h-9 flex-shrink-0 cursor-pointer shadow-xs"
            title="Search"
          >
            <Search size={14} strokeWidth={2.5} />
          </button>
        </div>
      </form>

      {/* Small subtle Clear All option after search */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between text-xs px-1 text-gray-500 -mt-2">
          <span className="text-gray-500 font-medium text-[11px] sm:text-xs">
            Showing filtered favorites ({totalCount})
          </span>
          <button
            type="button"
            onClick={handleClearAndReset}
            className="text-xs font-semibold text-rose-500 hover:text-rose-600 hover:underline inline-flex items-center gap-1 cursor-pointer transition-colors"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2ab391]"></div>
        </div>
      ) : favorites.length === 0 ? (
        hasActiveFilters ? (
          /* Filtered empty state */
          <div className="bg-white rounded-2xl border border-gray-100 p-10 sm:p-14 text-center">
            <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-200/80 shadow-2xs">
              <Search size={24} className="text-gray-400" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">
              No Matching Favorites Found
            </h3>
            <p className="text-gray-500 text-xs sm:text-sm max-w-md mx-auto mb-6">
              None of your saved favorite listings match the selected state, city, or category.
            </p>
            <button
              onClick={handleClearAndReset}
              className="inline-flex items-center gap-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-xl transition-all shadow-xs cursor-pointer"
            >
              <Search size={14} />
              <span>View All Favorites</span>
            </button>
          </div>
        ) : (
          /* General zero favorites state */
          <div className="bg-white rounded-2xl border border-gray-100 p-10 sm:p-14 text-center">
            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-rose-100 shadow-2xs">
              <Heart size={28} className="fill-rose-500/20" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1">
              No Favorites Saved Yet
            </h3>
            <p className="text-gray-500 text-xs sm:text-sm max-w-md mx-auto mb-6">
              Explore verified vendors and interior designers, and click the heart icon on any listing to bookmark it here for quick reference.
            </p>
            <button
              onClick={() => navigate('/listing')}
              className="inline-flex items-center gap-2 bg-[#2ab391] hover:bg-[#23977a] text-white text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-xl transition-all shadow-xs cursor-pointer"
            >
              <Search size={15} />
              <span>Discover Vendors</span>
            </button>
          </div>
        )
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {favorites.map((item) => {
              const listing = item.listing;
              if (!listing) return null;

              const displayTags = listing.tags || [];
              const showTags = displayTags.slice(0, 3);
              const extraCount = displayTags.length - showTags.length;

              return (
                <div
                  key={item.id}
                  onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all flex flex-col justify-between cursor-pointer group"
                >
                  <div className="p-4 sm:p-5">
                    {/* Top Row: Logo / Banner & Badges & Remove Button */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3.5 min-w-0">
                        {/* Logo Thumbnail */}
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {listing.logoUrl ? (
                            <img
                              src={getImageUrl(listing.logoUrl)}
                              alt={listing.companyName}
                              className="w-full h-full object-cover"
                            />
                          ) : listing.profileBannerUrl ? (
                            <img
                              src={getImageUrl(listing.profileBannerUrl)}
                              alt={listing.companyName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[#2ab391] font-bold text-xl">
                              {listing.companyName.charAt(0)}
                            </span>
                          )}
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate group-hover:text-[#2ab391] transition-colors">
                              {listing.companyName}
                            </h3>
                            {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                              <span className="inline-flex items-center gap-0.5 bg-[#2ab391] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-2xs">
                                <ShieldCheck size={10} /> Trusted
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-1">
                            <MapPin size={13} className="text-[#2ab391] flex-shrink-0" />
                            <span className="truncate">
                              {listing.area ? `${listing.area}, ` : ''}
                              {listing.city ? `${listing.city}, ` : ''}
                              {listing.state}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Favorite Heart / Remove button */}
                      <button
                        onClick={(e) => handleRemoveFavorite(listing.id, e)}
                        disabled={removingId === listing.id}
                        className="w-8 h-8 rounded-full bg-rose-50 border border-rose-100 text-rose-500 hover:bg-rose-100 flex items-center justify-center transition-all flex-shrink-0 cursor-pointer"
                        title="Remove from favorites"
                      >
                        {removingId === listing.id ? (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-rose-500"></div>
                        ) : (
                          <Heart size={16} className="fill-rose-500" />
                        )}
                      </button>
                    </div>

                    {/* Tags / Category */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {listing.categoryName && (
                        <span className="bg-[#e0f7f8] text-[#2ab391] text-[11px] font-semibold px-2.5 py-0.5 rounded-md">
                          {listing.categoryName}
                        </span>
                      )}
                      {showTags.map((tag) => (
                        <span
                          key={tag.id}
                          className="bg-gray-50 text-gray-600 text-[11px] font-medium px-2 py-0.5 rounded-md border border-gray-100"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {extraCount > 0 && (
                        <span className="bg-gray-100 text-gray-500 text-[11px] font-medium px-2 py-0.5 rounded-md">
                          +{extraCount} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div className="bg-gray-50/70 border-t border-gray-100 px-4 sm:px-5 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {listing.phone && (
                        <a
                          href={`tel:${listing.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors shadow-2xs"
                        >
                          <Phone size={12} />
                          <span>Call</span>
                        </a>
                      )}
                      {listing.phone && (
                        <a
                          href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 border border-gray-200 hover:bg-white text-gray-700 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all"
                        >
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="text-[#25D366]">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                          </svg>
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>

                    <button
                      onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-[#2ab391] transition-colors cursor-pointer"
                    >
                      <span>View Details</span>
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalCount}
              onPageChange={(p) => setCurrentPage(p)}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(0);
              }}
              pageSizeOptions={[12, 24, 48]}
              activeColorClass="bg-[#2ab391] border-[#2ab391] text-white shadow-xs"
              className="pt-2"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MyFavorites;
